import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { CalendarDays, MapPin, Music, Users, DollarSign, Heart, Trash2, Smartphone, Speaker, Video, Sliders, Target } from "lucide-react";
import { type ShowWithDetails } from "@shared/schema";
import { type PackageTier } from "@/lib/plan-storage";
import { formatDate } from "@/modules/calculator";
import { useState, useEffect } from "react";
import { getMatchBadgeClasses } from '@/lib/match-colors';
import { usePricingVisibility } from "@/lib/pricing-visibility";

interface ShowListItemProps {
  show: ShowWithDetails;
  // Selection functionality
  isSelectable?: boolean;
  isSelected?: boolean;
  onSelectionChange?: (showId: number, checked: boolean) => void;
  // Actions
  onViewShow?: (show: ShowWithDetails) => void;
  onSaveShow?: (show: ShowWithDetails) => void;
  onRemoveShow?: (showId: number) => void;
  // State
  isShowSaved?: (showId: number) => boolean;
  isSaving?: boolean;
  isAuthenticated?: boolean;
  // Display options
  showArtistInfo?: boolean; // Show artist info (for venue modals)
  showVenueInfo?: boolean; // Show venue info (for artist modals)
  showRemoveButton?: boolean; // Show remove button (for saved shows)
  // Audience matching
  audienceMatchPercentage?: number;
  matchingAttributes?: string[];
  showAudienceMatch?: boolean; // Whether to show % match instead of attributes
  // Plan status
  isInPlan?: boolean;
  planPackageTier?: PackageTier;
  planPackagePrice?: number;
}

export function ShowListItem({
  show,
  isSelectable = false,
  isSelected = false,
  onSelectionChange,
  onViewShow,
  onSaveShow,
  onRemoveShow,
  isShowSaved,
  isSaving = false,
  isAuthenticated = false,
  showArtistInfo = false,
  showVenueInfo = true,
  showRemoveButton = false,
  audienceMatchPercentage,
  matchingAttributes = [],
  showAudienceMatch = false,
  isInPlan = false,
  planPackageTier,
  planPackagePrice,
}: ShowListItemProps) {
  const [isHeartAnimating, setIsHeartAnimating] = useState(false);
  const { isPricingVisible } = usePricingVisibility();

  // Get package tier styling
  const getPackageStyles = () => {
    if (!isInPlan || !planPackageTier) return {};

    switch (planPackageTier) {
      case 'set-core':
        return {
          borderColor: 'border-l-gray-600',
          bgColor: 'bg-gray-50/50',
          badgeColor: 'bg-gray-600',
          icon: Smartphone,
          label: 'SET Core'
        };
      case 'set-audio':
        return {
          borderColor: 'border-l-blue-600',
          bgColor: 'bg-blue-50/50',
          badgeColor: 'bg-blue-600',
          icon: Speaker,
          label: 'SET Audio'
        };
      case 'set-video':
        return {
          borderColor: 'border-l-purple-600',
          bgColor: 'bg-purple-50/50',
          badgeColor: 'bg-purple-600',
          icon: Video,
          label: 'SET Video'
        };
      case 'custom':
        return {
          borderColor: 'border-l-green-600',
          bgColor: 'bg-green-50/50',
          badgeColor: 'bg-green-600',
          icon: Sliders,
          label: 'SET Custom'
        };
      default:
        return {};
    }
  };

  const packageStyles = getPackageStyles();

  // Use shared UTC-safe date formatter



  const handleSelectionChange = (checked: boolean) => {
    if (onSelectionChange) {
      onSelectionChange(show.id, checked);
    }
  };

  const handleSaveShow = () => {
    if (onSaveShow) {
      // Trigger heart animation only when saving (not unsaving)
      const currentlySaved = isShowSaved ? isShowSaved(show.id) : false;
      if (!currentlySaved) {
        setIsHeartAnimating(true);
        // Stop animation after it completes
        setTimeout(() => {
          setIsHeartAnimating(false);
        }, 800); // Match animation duration
      }
      onSaveShow(show);
    }
  };

  const handleRemoveShow = () => {
    if (onRemoveShow) {
      onRemoveShow(show.id);
    }
  };

  const saved = isShowSaved ? isShowSaved(show.id) : false;
  const defaultImage = "https://max.live/hubfs/2Crop-Cell-phone-concert-copy.jpg";
  const artistImage = show.artist.imageUrl && show.artist.imageUrl.trim() !== '' 
    ? show.artist.imageUrl 
    : defaultImage;

  return (
    <Card className={`p-4 ${isInPlan ? `border-l-4 ${packageStyles.borderColor} ${packageStyles.bgColor}` : ''}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3 flex-1">
          {isSelectable && isAuthenticated && (
            <Checkbox
              id={`show-${show.id}`}
              checked={isSelected}
              onCheckedChange={handleSelectionChange}
            />
          )}
          
          <Avatar className="h-12 w-12 flex-shrink-0">
            <AvatarImage 
              src={artistImage} 
              alt={show.artist.name}
              className="object-cover object-center"
              onError={(e) => {
                const img = e.currentTarget;
                if (img.src !== defaultImage) {
                  img.src = defaultImage;
                }
              }}
            />
            <AvatarFallback className="bg-gray-200 text-gray-600 font-medium">
              {show.artist.name.charAt(0)}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <CalendarDays className="w-4 h-4 text-blue-500" />
              <span className="font-medium">{formatDate(show.date)}</span>
            </div>
            
            {showArtistInfo && (
              <div className="flex items-center gap-2 mb-2">
                <Music className="w-4 h-4 text-purple-500" />
                <span className="text-sm font-medium">{show.artist.name}</span>
              </div>
            )}
            
            {showVenueInfo && (
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4 text-green-500" />
                <span className="text-sm">
                  {show.venue.name} - {show.venue.city}, {show.venue.state}
                </span>
              </div>
            )}
            
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                <span>{show.venue.capacity?.toLocaleString()} capacity</span>
              </div>
              
              {/* Show package tier badge if in plan */}
              {isInPlan && planPackageTier && (
                <Badge className={`${packageStyles.badgeColor} text-white text-xs flex items-center gap-1`}>
                  {packageStyles.icon && <packageStyles.icon className="w-3 h-3" />}
                  {packageStyles.label} • In Plan
                  {isPricingVisible && planPackagePrice && ` • $${planPackagePrice.toLocaleString()}`}
                </Badge>
              )}
              
              {/* Show audience match percentage if available and filtering is active */}
              {showAudienceMatch && audienceMatchPercentage !== undefined && !isInPlan && (
                <Badge className={`${getMatchBadgeClasses(audienceMatchPercentage)} text-xs flex items-center gap-1`}>
                  <Target className="w-3 h-3" />
                  {audienceMatchPercentage}% Match
                </Badge>
              )}
              
              {/* Sponsorship pricing removed per UI simplification requirements */}
            </div>
          </div>
        </div>
        
        <div className="flex flex-col gap-2">
          {showRemoveButton && onRemoveShow ? (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleRemoveShow}
              aria-label={`Remove ${show.artist?.name || 'Unknown Artist'} show from saved shows`}
            >
              <Trash2 className="h-4 w-4 mr-1" aria-hidden="true" />
              Remove
            </Button>
          ) : (
            <>
              {isAuthenticated && onSaveShow && (
                <Button
                  variant={saved ? "unsave" : "save"}
                  size="sm"
                  onClick={handleSaveShow}
                  disabled={isSaving}
                  aria-label={saved ? `Unsave ${show.artist?.name || 'Unknown Artist'} show` : `Save ${show.artist?.name || 'Unknown Artist'} show`}
                >
                  <Heart className={`h-4 w-4 mr-1 transition-all duration-300 ${
                    saved 
                      ? "fill-current text-red-600" 
                      : "hover:text-red-400 hover:scale-110"
                  } ${isHeartAnimating ? "animate-grow-and-pop" : ""}`} aria-hidden="true" />
                  {saved ? "Unsave Show" : "Save Show"}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </Card>
  );
}
